import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { MainLayout } from '@/components/layouts/MainLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Copy, Eye, EyeOff, Key, Plus, Trash2, ExternalLink, Code } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface ApiKey {
  id: string;
  api_key: string;
  name: string;
  quota_daily: number;
  usage_count: number;
  last_reset_date: string;
  active: boolean;
  allowed_origins: string[] | null;
  created_at: string;
}

interface UsageLog {
  id: string;
  endpoint: string;
  data_points: number;
  ai_analysis_used: boolean;
  response_time_ms: number;
  status_code: number;
  error_message: string | null;
  created_at: string;
}

export default function ApiKeys() {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [usageLogs, setUsageLogs] = useState<UsageLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [showKeys, setShowKeys] = useState<{ [key: string]: boolean }>({});
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyQuota, setNewKeyQuota] = useState(50);
  const [newKeyOrigins, setNewKeyOrigins] = useState('');
  const [createdKey, setCreatedKey] = useState<string | null>(null);

  useEffect(() => {
    loadApiKeys();
    loadUsageLogs();
  }, []);

  const loadApiKeys = async () => {
    try {
      const { data, error } = await supabase
        .from('api_keys')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setApiKeys(data || []);
    } catch (error) {
      console.error('Error loading API keys:', error);
      toast.error('Erreur lors du chargement des clés API');
    } finally {
      setLoading(false);
    }
  };

  const loadUsageLogs = async () => {
    try {
      const { data, error } = await supabase
        .from('api_usage_logs')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setUsageLogs(data || []);
    } catch (error) {
      console.error('Error loading usage logs:', error);
    }
  };

  const createApiKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Le nom est requis');
      return;
    }

    try {
      const origins = newKeyOrigins
        .split(',')
        .map(o => o.trim())
        .filter(o => o.length > 0);

      const { data, error } = await supabase.functions.invoke('generate-api-key', {
        body: {
          name: newKeyName,
          quotaDaily: newKeyQuota,
          allowedOrigins: origins,
        },
      });

      if (error) throw error;

      if (data?.success) {
        setCreatedKey(data.data.apiKey);
        toast.success('Clé API créée avec succès');
        loadApiKeys();
        setNewKeyName('');
        setNewKeyQuota(50);
        setNewKeyOrigins('');
      } else {
        throw new Error(data?.error || 'Échec de la création');
      }
    } catch (error: any) {
      console.error('Error creating API key:', error);
      toast.error(error.message || 'Erreur lors de la création de la clé API');
    }
  };

  const deleteApiKey = async (id: string) => {
    try {
      const { error } = await supabase
        .from('api_keys')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast.success('Clé API supprimée');
      loadApiKeys();
    } catch (error) {
      console.error('Error deleting API key:', error);
      toast.error('Erreur lors de la suppression de la clé API');
    }
  };

  const toggleActive = async (id: string, currentStatus: boolean) => {
    try {
      const { error } = await supabase
        .from('api_keys')
        .update({ active: !currentStatus })
        .eq('id', id);

      if (error) throw error;

      toast.success(currentStatus ? 'Clé désactivée' : 'Clé activée');
      loadApiKeys();
    } catch (error) {
      console.error('Error toggling API key:', error);
      toast.error('Erreur lors de la modification de la clé');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papiers');
  };

  const maskApiKey = (key: string) => {
    return key.substring(0, 8) + '...' + key.substring(key.length - 8);
  };

  const exampleCode = `// Exemple d'utilisation de l'API
const response = await fetch('https://xamhihpqeuhtsquvbrdz.supabase.co/functions/v1/chart-api', {
  method: 'POST',
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': 'VOTRE_CLE_API'
  },
  body: JSON.stringify({
    data: {
      name: "Ventes 2024",
      columns: [
        { id: "product", name: "Produit", type: "text" },
        { id: "sales", name: "Ventes", type: "number" }
      ],
      rows: [
        { product: "Laptop", sales: 150000 },
        { product: "Phone", sales: 89000 }
      ]
    },
    options: {
      chartTypes: ["bar", "pie", "line"],
      xColumn: "product",
      yColumn: "sales",
      maxItems: 10,
      sortOrder: "desc",
      includeAiAnalysis: true
    }
  })
});

const result = await response.json();
console.log(result);`;

  return (
    <MainLayout title="Clés API">
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Clés API</h1>
            <p className="text-muted-foreground">
              Gérez vos clés API pour accéder à l'API Charts & AI Analysis
            </p>
          </div>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="h-4 w-4 mr-2" />
                Nouvelle clé
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Créer une nouvelle clé API</DialogTitle>
                <DialogDescription>
                  Cette clé vous permettra d'accéder à l'API depuis n'importe quelle application
                </DialogDescription>
              </DialogHeader>
              
              {createdKey ? (
                <div className="space-y-4">
                  <div className="p-4 bg-green-50 dark:bg-green-950 rounded-lg border border-green-200 dark:border-green-800">
                    <p className="text-sm font-medium mb-2">Votre nouvelle clé API :</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 p-2 bg-white dark:bg-gray-900 rounded text-xs break-all">
                        {createdKey}
                      </code>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyToClipboard(createdKey)}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      ⚠️ Sauvegardez cette clé maintenant. Elle ne sera plus affichée.
                    </p>
                  </div>
                  <Button
                    onClick={() => {
                      setCreatedKey(null);
                      setIsCreateDialogOpen(false);
                    }}
                    className="w-full"
                  >
                    Fermer
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="keyName">Nom de la clé</Label>
                    <Input
                      id="keyName"
                      value={newKeyName}
                      onChange={(e) => setNewKeyName(e.target.value)}
                      placeholder="Mon application web"
                    />
                  </div>
                  <div>
                    <Label htmlFor="quota">Quota quotidien</Label>
                    <Input
                      id="quota"
                      type="number"
                      value={newKeyQuota}
                      onChange={(e) => setNewKeyQuota(Number(e.target.value))}
                      min={1}
                      max={1000}
                    />
                  </div>
                  <div>
                    <Label htmlFor="origins">Origines autorisées (optionnel)</Label>
                    <Input
                      id="origins"
                      value={newKeyOrigins}
                      onChange={(e) => setNewKeyOrigins(e.target.value)}
                      placeholder="https://monsite.com, https://app.monsite.com"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Séparez les URLs par des virgules. Laissez vide pour autoriser toutes les origines.
                    </p>
                  </div>
                  <Button onClick={createApiKey} className="w-full">
                    Créer la clé
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>
        </div>

        <Tabs defaultValue="keys" className="space-y-4">
          <TabsList>
            <TabsTrigger value="keys">Mes clés</TabsTrigger>
            <TabsTrigger value="usage">Utilisation</TabsTrigger>
            <TabsTrigger value="docs">Documentation</TabsTrigger>
          </TabsList>

          <TabsContent value="keys" className="space-y-4">
            {loading ? (
              <Card>
                <CardContent className="py-8">
                  <p className="text-center text-muted-foreground">Chargement...</p>
                </CardContent>
              </Card>
            ) : apiKeys.length === 0 ? (
              <Card>
                <CardContent className="py-8">
                  <div className="text-center">
                    <Key className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground mb-4">
                      Aucune clé API créée pour le moment
                    </p>
                    <Button onClick={() => setIsCreateDialogOpen(true)}>
                      <Plus className="h-4 w-4 mr-2" />
                      Créer ma première clé
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              apiKeys.map((key) => (
                <Card key={key.id}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          {key.name}
                          <Badge variant={key.active ? 'default' : 'secondary'}>
                            {key.active ? 'Active' : 'Inactive'}
                          </Badge>
                        </CardTitle>
                        <CardDescription>
                          Créée le {new Date(key.created_at).toLocaleDateString('fr-FR')}
                        </CardDescription>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleActive(key.id, key.active)}
                        >
                          {key.active ? 'Désactiver' : 'Activer'}
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="destructive">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Supprimer cette clé API ?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Cette action est irréversible. Toutes les applications utilisant cette clé
                                ne pourront plus accéder à l'API.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Annuler</AlertDialogCancel>
                              <AlertDialogAction onClick={() => deleteApiKey(key.id)}>
                                Supprimer
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-xs">Clé API</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <code className="flex-1 p-2 bg-muted rounded text-sm">
                          {showKeys[key.id] ? key.api_key : maskApiKey(key.api_key)}
                        </code>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() =>
                            setShowKeys((prev) => ({ ...prev, [key.id]: !prev[key.id] }))
                          }
                        >
                          {showKeys[key.id] ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copyToClipboard(key.api_key)}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-muted-foreground">Quota quotidien</Label>
                        <p className="text-lg font-medium">
                          {key.usage_count} / {key.quota_daily}
                        </p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">Dernière réinitialisation</Label>
                        <p className="text-sm">
                          {new Date(key.last_reset_date).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                    </div>

                    {key.allowed_origins && key.allowed_origins.length > 0 && (
                      <div>
                        <Label className="text-xs text-muted-foreground">Origines autorisées</Label>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {key.allowed_origins.map((origin, i) => (
                            <Badge key={i} variant="outline">
                              {origin}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          <TabsContent value="usage" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Historique d'utilisation</CardTitle>
                <CardDescription>Les 50 dernières requêtes API</CardDescription>
              </CardHeader>
              <CardContent>
                {usageLogs.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    Aucune utilisation enregistrée
                  </p>
                ) : (
                  <div className="space-y-2">
                    {usageLogs.map((log) => (
                      <div
                        key={log.id}
                        className="flex items-center justify-between p-3 border rounded-lg"
                      >
                        <div className="flex-1">
                          <p className="font-medium">{log.endpoint}</p>
                          <p className="text-sm text-muted-foreground">
                            {log.data_points} points de données
                            {log.ai_analysis_used && ' • Analyse IA'}
                          </p>
                        </div>
                        <div className="text-right">
                          <Badge
                            variant={log.status_code === 200 ? 'default' : 'destructive'}
                          >
                            {log.status_code}
                          </Badge>
                          <p className="text-xs text-muted-foreground mt-1">
                            {log.response_time_ms}ms
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(log.created_at).toLocaleString('fr-FR')}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="docs" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Documentation de l'API</CardTitle>
                <CardDescription>Guide de démarrage rapide</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">Endpoint</h3>
                  <code className="block p-3 bg-muted rounded">
                    POST https://xamhihpqeuhtsquvbrdz.supabase.co/functions/v1/chart-api
                  </code>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">En-têtes requis</h3>
                  <ul className="space-y-1 text-sm">
                    <li><code>Content-Type: application/json</code></li>
                    <li><code>X-API-Key: VOTRE_CLE_API</code></li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Exemple de code</h3>
                  <div className="relative">
                    <pre className="p-4 bg-muted rounded overflow-x-auto text-xs">
                      <code>{exampleCode}</code>
                    </pre>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="absolute top-2 right-2"
                      onClick={() => copyToClipboard(exampleCode)}
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div>
                  <h3 className="font-semibold mb-2">Liens utiles</h3>
                  <div className="flex flex-col gap-2">
                    <a
                      href="https://supabase.com/dashboard/project/xamhihpqeuhtsquvbrdz/functions/chart-api/logs"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Logs de la fonction chart-api
                    </a>
                    <a
                      href="https://supabase.com/dashboard/project/xamhihpqeuhtsquvbrdz/functions"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-primary hover:underline"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Gestion des Edge Functions
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </MainLayout>
  );
}
